import { combineReducers } from 'redux';
import { findKey, without, uniq, first, get } from 'lodash';
import moment from 'moment';

import {
  FETCH_PURCHASE,
  FETCH_CLAIM_PURCHASE,
  FETCH_USER_PURCHASES,
  FETCH_DUPLICATED_PURCHASE,
  SET_CURRENT_PURCHASE,
  UPDATE_PURCHASE,
  CANCEL_PURCHASE,
  REMOVE_MEMBERSHIP,
  FETCH_COMMIT_TO_PURCHASE,
  UNAUTHENTICATE_USER,
  FETCH_ENABLE_AUTO_PILOT,
  FETCH_DISABLE_AUTO_PILOT,
  FETCH_CONSUMER_FIELD_SCHEMAS,
  FETCH_PURCHASE_ACTIVITIES,
} from '../actions/index';

import {
  AUTHORIZED,
} from '../lib/payment-statuses';

import {
  normalizePurchase,
  normalizePurchases,
} from './schemas';

export const IGNORED_PURCHASE_ATTRS = [
  'groupCommitDeadline',
  'productStartDateTimeTimezone',
  'groupCommitDeadlineMannuallyUpdated',
  'costComponents',
  'isAgentCreated',
  'merchantId',
  'paymentGatewayId',
  'payoutAccountId',
  'applyConsumerFeeToOrganizer',
  'autoPilotTriggerSlots',
  'groupNoteToOrganizer',
  'languageCode',
  'confirmationUrl',
  'completedAt',
  'slotName',
  'canceledAt',
  'metadata',
  'organizerCanEditCost',
  'updatedBy',
];

const setPurchase = (state, action) => {
  const { payload } = action;
  const { id } = payload;

  if (!id) return state;

  const normalized = normalizePurchase(payload);
  const purchase = normalized.entities.purchases[normalized.result];

  return {
    ...state,

    [id]: {
      ...state[id],
      ...purchase,
    },
  };
};

const addUserPurchases = (state, action) => {
  const { payload } = action;
  const { entities: { purchases } } = normalizePurchases(payload.purchases);

  return {
    ...state,
    ...purchases,
  };
};

const removeMembership = (state, action) => {
  const { payload } = action;
  const { id } = payload;

  const result = {};

  Object.keys(state).forEach((key) => {
    result[key] = result[key] || { ...state[key] };

    result[key].memberships = without(state[key].memberships, id);
  });

  return result;
};

const addMembership = (state, action) => {
  const { payload: { id } } = action;
  const result = {};

  Object.keys(state).forEach((key) => {
    result[key] = result[key] || { ...state[key] };

    result[key].memberships = uniq([...state[key].memberships, id]);
  });

  return result;
};

const addConsumerFieldSchemas = (state, action) => {
  const purchaseId = findKey(state, { slug: action.payload.purchaseSlug });
  const changes = {
    payload: {
      id: purchaseId,
      // TODO: this mapping should happen with normalizr
      consumerFieldSchemas: action.payload.data.map(schema => schema.id),
    },
  };
  return setPurchase(state, changes);
}

const groupActivities = (activities, payments = []) => {
  const groupedByDate = activities
    // Reverse the array
    .reverse()
    // Create a copy of each activity with
    // a new "attributes" attr that cointains all the valid
    // actionable attributes
    .map((activity) => {
      const attributes = { ...activity.data };

      IGNORED_PURCHASE_ATTRS.forEach((attr) => {
        delete attributes[attr];
      });

      let type = activity.type;

      if (activity.type === 'payment_authorized') {
        type = `${activity.type}_${activity.data.paymentType}`
      }

      return {
        ...activity,
        type,
        attributes,
      };
    })
    // Remove all the activities that doesn't have actionable attributes
    .filter(activity => Object.keys(activity.attributes).length > 0)
    // Remove all payment_authorized activities on which their payment had changed it status
    .filter((activity) => {
      const { type, data: { paymentId } } = activity;
      if (type.search('payment_authorized') === -1 ) {
        return true;
      }

      if (!payments.length) return false;

      const payment = get(
        payments.filter(payment => payment.id === paymentId),
        '[0]'
      );

      if (!payment) return false;

      if (payment.status !== AUTHORIZED) return false;

      return true;
    })
    // Group the activities by date
    .reduce((groups, activity) => {
      const date = moment(activity.occurredAt).local()
      .format("YYYY-MM-DD");

        if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(activity);
      return groups;
    }, {});

  Object.keys(groupedByDate).forEach((key) => {
    groupedByDate[key] = groupedByDate[key]
      .reduce((_activities, activity) => {
        // Add the activity to the _activities array
        // if the activity is NOT `member_invited`
        if (activity.type !== 'member_invited') {
          _activities.push(activity);
        }

        if (activity.type === 'member_invited') {
          // Search for a member_invited accomulator
          let accumulatorActivity = _activities
            .filter(activity => activity.isAccumulator);

          if (accumulatorActivity.length > 0) {
            accumulatorActivity = accumulatorActivity[0];
            const now = moment(accumulatorActivity.occurredAt);
            const then = moment(activity.occurredAt);
            const diff = moment.utc(moment(now).diff(moment(then)));
            if (diff.minutes() <= 5) {
              accumulatorActivity.data.invited += 1;
            } else {
              _activities.forEach(a => delete a.isAccumulator);
              activity.isAccumulator = true;
              activity.data.invited = 1;
              _activities.push(activity)
            }
          } else {
            accumulatorActivity = {
              ...activity,
              data: {
                inviterUserId: activity.data.inviterUserId,
                invited: 1,
              },
              isAccumulator: true,
            }
            _activities.push(accumulatorActivity);
          }
        }

        return _activities;
      }, []);
  });

  return groupedByDate;
}

const addPurchaseActivities = (state = {}, action) => {
  if (!action.payload.purchaseId) {
    return { ...state };
  }

  const purchase = first(Object.keys(state).map(id => state[id])
    .filter(purchase => purchase.id === action.payload.purchaseId));

  return {
    ...state,
    [purchase.id]: {
      ...purchase,
      activities: groupActivities(action.payload.activities, purchase.payments),
    }
  };
}

const purchasesById = (state = {}, action) => {
  switch (action.type) {
    case FETCH_PURCHASE:
    case FETCH_DUPLICATED_PURCHASE:
    case UPDATE_PURCHASE:
    case CANCEL_PURCHASE:
    case FETCH_CLAIM_PURCHASE:
    case FETCH_ENABLE_AUTO_PILOT:
    case FETCH_DISABLE_AUTO_PILOT:
      return setPurchase(state, action);
    case FETCH_COMMIT_TO_PURCHASE:
      return addMembership(state, action);
    case FETCH_CONSUMER_FIELD_SCHEMAS:
      return addConsumerFieldSchemas(state, action);
    case REMOVE_MEMBERSHIP:
      return removeMembership(state, action);
    case FETCH_USER_PURCHASES:
      return addUserPurchases(state, action);
    case FETCH_PURCHASE_ACTIVITIES:
      return addPurchaseActivities(state, action);
    default:
      return state;
  }
};

const currentPurchase = (state = null, action) => {
  switch (action.type) {
    case SET_CURRENT_PURCHASE:
      return action.payload.id;

    default:
      return state;
  }
};

const duplicatedPurchase = (state = null, action) => {
  switch (action.type) {
    case FETCH_DUPLICATED_PURCHASE:
      return action.payload.id;

    case UNAUTHENTICATE_USER:
      return null;

    default:
      return state;
  }
};

export default combineReducers({
  byId: purchasesById,
  current: currentPurchase,
  duplicated: duplicatedPurchase,
});



// WEBPACK FOOTER //
// ./src/reducers/purchases.reducer.js